import React, { lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import { Redirect, Route, Switch, withRouter } from 'react-router';
import { Layout } from './Layout';
import ErrorBoundary from './Errors/ErrorBoundary';
import ScrollToTop from './ScrollToTop';
import LandingFooter from './Navigation/LandingFooter';
import { withIsMobile } from 'treeline-react-core-components/WindowSizeProvider';
import paths from '../paths';
import WindowSizeThresholdsEnum from '../enums/WindowSizeThresholdsEnum';
import EdelweissConnectInitializer from './EdelweissConnect/EdelweissConnectInitializer';
import PasswordReset from './Anonymous/PasswordReset/PasswordReset';
import ReactGA from 'react-ga';
import AccountOrMembershipRequiredModalContext from './Anonymous/AccountOrMembershipRequiredModal/contexts/AccountOrMembershipRequiredModalContext';
import AccountOrMembershipRequiredModal from './Anonymous/AccountOrMembershipRequiredModal/AccountOrMembershipRequiredModal';
import AnonymousHeader from './Anonymous/AnonymousHeader/AnonymousHeader';
import AppHeader from './AppHeader';
import LoadingApp from './UI/LoadingApp';
import { Translate } from '@above-the-treeline/localization';

// data stores
import { connectToUser } from './User/containers/UserDataStore';
import CommunitiesDataStore from './Communities/containers/communitiesDataStore';
import DownloadsDataStore from './Downloads/containers/DownloadsDataStore';
import RequestsDataStore from './Requests/containers/RequestsDataStore';
import ShelvesDataStore from './Shelves/containers/ShelvesDataStore';
import NoticesDataStore from './User/Notices/containers/NoticesDataStore';
import ReviewsDataStore from './Reviews/containers/ReviewsDataStore';
import CollaborativeListsDataStore from './CollaborativeLists/containers/CollaborativeListsDataStore';
import CommunityReviewsDataStore from './Community/containers/CommunityReviewsDataStore';
import PreferencesDataStore from './Preferences/containers/PreferencesDataStore';
import SavedFiltersDataStore from './SavedFilters/containers/SavedFiltersDataStore';
import CollectionsDataStore from './Collections/containers/CollectionsDataStore';
import CatalogFoldersDataStore from './Folders/Catalogs/containers/CatalogFoldersDataStore';
import FilterOptionsDataStore from './SavedFilters/containers/FilterOptionsDataStore';
import { EventsDataStoreProvider } from './Events/contexts/EventsDataStoreContext';
import LocalizationContainer from './Localization/LocalizationContainer';

// css
import '../crm.css';
import '../general.css';
import '../index.css';
import '../NewLook.css';

// routes to lazy load:
const MainPage = lazy(() => import('./Home/MainPage'));
const Search = lazy(() => import('./Search/Search'));
const YourBooks = lazy(() => import('./YourBooks/YourBooks'));
const CommunityHomeWithEventDetails = lazy(() =>
	import('./Community/CommunityHomeWithEventDetails'),
);
const CommunityHome = lazy(() => import('./Home/CommunityHome'));
const ConversationHome = lazy(() =>
	import('./Conversations/Home/ConversationHome'),
);
const ReviewCopies = lazy(() => import('./ReviewCopies/ReviewCopies'));
const BuzzTitleList = lazy(() => import('./Buzz/BuzzTitleList'));
const SavedFilter = lazy(() => import('./SavedFilters/SavedFilter'));
const Catalog = lazy(() => import('./Collections/Catalog'));
const CollaborativeListRoute = lazy(() =>
	import('./CollaborativeLists/CollaborativeListRoute'),
);
const CollaborativeListsHome = lazy(() =>
	import('./CollaborativeLists/Home/CollaborativeListsHome'),
);
const CommunicationSettings = lazy(() =>
	import('./CommunicationSettings/CommunicationSettings'),
);
const ProfileEditor = lazy(() => import('./ProfileEditor/ProfileEditor'));
const CollaborativeListsIFrameHome = lazy(() =>
	import('./CollaborativeLists/Home/CollaborativeListsIFrameHome'),
);
const Shelves = lazy(() => import('./Shelves/Shelves'));
const Requests = lazy(() => import('./Requests/Requests'));
const Downloads = lazy(() => import('./Downloads/Downloads'));
const Notices = lazy(() => import('./User/Notices/Notices'));
const Reviews = lazy(() => import('./Reviews/Reviews'));
const Help = lazy(() => import('./Home/Help'));
const Outreach = lazy(() => import('./Publisher/Outreach'));
const MainPageWithTitleDetails = lazy(() =>
	import('./Home/MainPageWithTitleDetails'),
);
const RegistrationPage = lazy(() => import('./Anonymous/RegistrationPage'));

class AppLayoutFrame extends React.Component {
	static propTypes = {
		isMobile: PropTypes.func,
		location: PropTypes.object,
		history: PropTypes.object,
		userDataStore: PropTypes.object,
	};

	componentDidMount() {
		this.props.history.listen(location => {
			ReactGA.set({ page: location.pathname });
			ReactGA.pageview(location.pathname);
		});
	}

	render() {
		const {
			location,
			userDataStore: { isAnonymous },
		} = this.props;
		const { pathname } = location;
		const isIFrame = pathname.startsWith('/iframe');

		return (
			<ErrorBoundary doLocalize={false}>
				<LocalizationContainer>
					<ErrorBoundary>
						<Suspense fallback={<LoadingApp />}>
							{isAnonymous()
								? this.renderAnonymousLayoutFrame(isIFrame)
								: this.renderLayoutFrame(isIFrame)}
						</Suspense>
					</ErrorBoundary>
				</LocalizationContainer>
			</ErrorBoundary>
		);
	}

	showHelpDeskWidget = () => window.FreshworksWidget('show', 'launcher');

	hideHelpDeskWidget = () => window.FreshworksWidget('hide', 'launcher');

	showOrHideHelpDeskWidget = isMobile =>
		isMobile ? this.hideHelpDeskWidget() : this.showHelpDeskWidget();

	renderLayoutFrame = isIFrame => {
		return (
			<CollaborativeListsDataStore>
				<CommunitiesDataStore>
					<PreferencesDataStore>
						<SavedFiltersDataStore>
							<CatalogFoldersDataStore>
								<CollectionsDataStore>
									<ShelvesDataStore>
										<RequestsDataStore>
											<ReviewsDataStore>
												<DownloadsDataStore>
													<NoticesDataStore>
														<FilterOptionsDataStore>
															<CommunityReviewsDataStore
																communityId={0}
																maxTitles={20}>
																<AccountOrMembershipRequiredModalContext>
																	<EventsDataStoreProvider>
																		<AccountOrMembershipRequiredModal />
																		{isIFrame
																			? this.renderIFrameAppLayoutFrame()
																			: this.renderAppLayoutFrame()}
																	</EventsDataStoreProvider>
																</AccountOrMembershipRequiredModalContext>
															</CommunityReviewsDataStore>
														</FilterOptionsDataStore>
													</NoticesDataStore>
												</DownloadsDataStore>
											</ReviewsDataStore>
										</RequestsDataStore>
									</ShelvesDataStore>
								</CollectionsDataStore>
							</CatalogFoldersDataStore>
						</SavedFiltersDataStore>
					</PreferencesDataStore>
				</CommunitiesDataStore>
			</CollaborativeListsDataStore>
		);
	};

	renderIFrameAppLayoutFrame = () => {
		this.hideHelpDeskWidget();

		return (
			<>
				<ScrollToTop />
				<Layout>
					<Route
						path={paths.collaborativeListsIFrameHome()}
						component={CollaborativeListsIFrameHome}
					/>
					<Route
						path={paths.communicationSettingsIFrameHome()}
						component={CommunicationSettings}
					/>
				</Layout>
			</>
		);
	};

	renderAnonymousLayoutFrame = isIFrame => {
		if (this.doShowLoginToEdelweissPlusPrompt(isIFrame)) {
			return this.renderLoginToEdelweissPlusPrompt();
		}

		const isMobile = this.props.isMobile();
		this.showOrHideHelpDeskWidget(isMobile);
		return (
			<CollaborativeListsDataStore>
				<CommunitiesDataStore>
					<PreferencesDataStore>
						<ShelvesDataStore>
							<RequestsDataStore>
								<ReviewsDataStore>
									<DownloadsDataStore>
										<CommunityReviewsDataStore
											communityId={0}
											maxTitles={20}>
											<EdelweissConnectInitializer>
												<AccountOrMembershipRequiredModalContext>
													<EventsDataStoreProvider>
														<AnonymousHeader />
														<AccountOrMembershipRequiredModal />
														<ScrollToTop />
														{this.renderAnonymousAppLayoutFrame()}
													</EventsDataStoreProvider>
												</AccountOrMembershipRequiredModalContext>
											</EdelweissConnectInitializer>
										</CommunityReviewsDataStore>
									</DownloadsDataStore>
								</ReviewsDataStore>
							</RequestsDataStore>
						</ShelvesDataStore>
					</PreferencesDataStore>
				</CommunitiesDataStore>
			</CollaborativeListsDataStore>
		);
	};

	doShowLoginToEdelweissPlusPrompt = isIFrame => {
		const { location } = this.props;
		const { pathname } = location;

		return (
			isIFrame &&
			pathname.startsWith(paths.communicationSettingsIFrameHome())
		);
	};

	renderLoginToEdelweissPlusPrompt = () => {
		return (
			<div className="pad-10">
				<Translate id="you_must_be_logged_in_to_edelweiss_plus_to_view_this_page" />
			</div>
		);
	};

	renderAppLayoutFrame = () => {
		const isMobile = this.props.isMobile();
		this.showOrHideHelpDeskWidget(isMobile);

		return (
			<EdelweissConnectInitializer>
				<AppHeader />
				<ScrollToTop />
				<Layout>
					<Switch>
						<Route
							exact
							path={paths.mainPage()}
							component={MainPage}
						/>
						<Route
							exact
							path={paths.resetPassword()}
							render={props => (
								<PasswordReset
									userToken={decodeURIComponent(
										props.match.params.userToken,
									)}
								/>
							)}
						/>
						<Route exact path={paths.home()} component={MainPage} />
						<Route path={paths.search()} component={Search} />
						<Route path={paths.yourBooks()} component={YourBooks} />
						<Route
							path={paths.communityHomeWithEventDetails()}
							component={CommunityHomeWithEventDetails}
						/>
						<Route
							path={paths.community()}
							component={CommunityHome}
						/>
						<Route
							path={paths.conversations()}
							component={ConversationHome}
						/>
						<Route
							path={paths.reviewCopies()}
							component={ReviewCopies}
						/>
						<Route
							path={paths.buzzLists()}
							component={BuzzTitleList}
						/>
						<Route
							path={paths.savedFilter()}
							component={SavedFilter}
						/>
						<Route path={paths.catalog()} component={Catalog} />
						<Route
							path={paths.collaborativeList()}
							component={CollaborativeListRoute}
						/>
						<Route
							path={paths.collaborativeLists()}
							component={CollaborativeListsHome}
						/>
						<Route
							path={paths.communicationSettings()}
							component={CommunicationSettings}
						/>
						<Route
							path={paths.profileEditor()}
							component={ProfileEditor}
						/>
						<Route
							path={paths.collaborativeListsIFrameHome()}
							component={CollaborativeListsIFrameHome}
						/>
						<Route path={paths.shelfType()} component={Shelves} />
						<Route path={paths.requests()} component={Requests} />
						<Route path={paths.downloads()} component={Downloads} />
						<Route
							path={paths.notifications()}
							component={Notices}
						/>
						<Route path={paths.reviews()} component={Reviews} />
						<Route path={paths.help()} component={Help} />
						<Route path={paths.outreach()} component={Outreach} />
						<Route
							path={paths.title()}
							component={MainPageWithTitleDetails}
						/>
						<Redirect to={paths.mainPage()} component={MainPage} />
					</Switch>
				</Layout>
				{isMobile && <LandingFooter />}
			</EdelweissConnectInitializer>
		);
	};

	renderAnonymousAppLayoutFrame = () => {
		return (
			<Layout>
				<Switch>
					<Route exact path={paths.mainPage()} component={MainPage} />
					<Route exact path={paths.home()} component={MainPage} />
					<Route
						exact
						path={paths.resetPassword()}
						render={props => (
							<PasswordReset
								userToken={decodeURIComponent(
									props.match.params.userToken,
								)}
							/>
						)}
					/>
					<Route
						path={paths.communicationSettings()}
						component={CommunicationSettings}
					/>
					<Route
						exact
						path={paths.register()}
						component={RegistrationPage}
					/>
					<Route
						path={paths.communityHomeWithEventDetails()}
						component={CommunityHomeWithEventDetails}
					/>
					<Route path={paths.community()} component={CommunityHome} />
					<Route
						path={paths.collaborativeList()}
						component={CollaborativeListRoute}
					/>
					<Route
						path={paths.title()}
						component={MainPageWithTitleDetails}
					/>
					<Route path={paths.help()} component={Help} />
					<Redirect to={paths.mainPage()} component={MainPage} />
				</Switch>
			</Layout>
		);
	};
}

export default connectToUser(
	withRouter(withIsMobile(AppLayoutFrame, WindowSizeThresholdsEnum.Medium)),
);
